import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-[#FF2E63] mb-2">Privacy Policy</h1>
        <p className="text-[#A1A1AA] mb-8">Last updated: February 2025</p>

        <div className="space-y-8 text-[#E4E4E7]">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Our Commitment to You</h2>
            <p className="leading-relaxed">
              Your privacy matters. This policy explains what information we collect, how we use it, and how we protect it. We believe in transparency and giving you control over your data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-[#08D9D6] mt-4 mb-2">Information you provide:</h3>
            <ul className="list-disc list-inside space-y-2 text-[#A1A1AA]">
              <li><strong className="text-white">Account info:</strong> Email, username, password, date of birth</li>
              <li><strong className="text-white">Profile info:</strong> Photos, bio, physical attributes, interests</li>
              <li><strong className="text-white">Messages:</strong> Content of chats with other users</li>
              <li><strong className="text-white">Location:</strong> Only when you grant permission, to show nearby users</li>
            </ul>

            <h3 className="text-lg font-medium text-[#08D9D6] mt-4 mb-2">Information collected automatically:</h3>
            <ul className="list-disc list-inside space-y-2 text-[#A1A1AA]">
              <li><strong className="text-white">Usage data:</strong> How you interact with the app</li>
              <li><strong className="text-white">Device info:</strong> Browser type, operating system</li>
              <li><strong className="text-white">Log data:</strong> Access times, pages viewed</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 text-[#A1A1AA]">
              <li>Show your profile to other users based on preferences</li>
              <li>Enable messaging and connections between users</li>
              <li>Send notifications about matches, messages, and events</li>
              <li>Improve the app based on usage patterns</li>
              <li>Detect and prevent fraud, abuse, and policy violations</li>
              <li>Respond to your support requests</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Who Sees Your Information</h2>
            
            <h3 className="text-lg font-medium text-[#08D9D6] mt-4 mb-2">Other users can see:</h3>
            <ul className="list-disc list-inside space-y-2 text-[#A1A1AA]">
              <li>Your public profile (photos, bio, basic info)</li>
              <li>Your approximate distance (not exact location)</li>
              <li>Messages you send them</li>
            </ul>

            <h3 className="text-lg font-medium text-[#08D9D6] mt-4 mb-2">We do NOT:</h3>
            <ul className="list-disc list-inside space-y-2 text-[#A1A1AA]">
              <li>Sell your personal data to third parties</li>
              <li>Share your exact location with other users</li>
              <li>Read your private messages (unless investigating reports)</li>
              <li>Share your email address with other users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Your Privacy Controls</h2>
            <p className="leading-relaxed mb-3">You have control over your privacy:</p>
            <ul className="list-disc list-inside space-y-2 text-[#A1A1AA]">
              <li><strong className="text-white">Visibility:</strong> Hide your profile from the grid anytime</li>
              <li><strong className="text-white">Incognito mode:</strong> Browse without appearing in "Viewed" lists</li>
              <li><strong className="text-white">Blocking:</strong> Block users to prevent all contact</li>
              <li><strong className="text-white">Notifications:</strong> Control what alerts you receive</li>
              <li><strong className="text-white">Delete account:</strong> Remove your data permanently</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Data Security</h2>
            <p className="leading-relaxed">
              We use industry-standard security measures including encryption, secure servers, and regular security audits. However, no system is 100% secure. Use a strong, unique password and be cautious about what you share.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Data Retention</h2>
            <p className="leading-relaxed">
              We keep your data while your account is active. If you delete your account, we remove your profile and messages. Some data may be retained for legal compliance or fraud prevention.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Beta Testing Note</h2>
            <p className="leading-relaxed bg-[#FF2E63]/10 border border-[#FF2E63]/30 rounded-lg p-4">
              During the beta period, we may collect additional feedback and usage data to improve the app. This data helps us understand how features are used and identify issues. Your participation helps build a better experience for everyone.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Changes to This Policy</h2>
            <p className="leading-relaxed">
              We may update this policy as PULSE evolves. We'll notify you of significant changes through the app. Continued use after changes means you accept the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Contact Us</h2>
            <p className="leading-relaxed">
              Questions about privacy? Use the feedback feature in the app or contact our support team. We're here to help.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 text-center text-[#52525B] text-sm">
          <p>PULSE - Your privacy is our priority</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
